import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mic, Sliders, Music2, Radio, Users, Film, CalendarRange, Sparkles, Wand2, Layers } from "lucide-react";
import api from "@/lib/api";
import { IMG, HERO_VIDEO_ID } from "@/lib/brand";
import { Section, Eyebrow, SoundBars } from "@/components/common";
import YouTubeBackground from "@/components/YouTubeBackground";

const SERVICE_ICONS = {
  "Production musicale": Music2,
  "Direction artistique": Wand2,
  "Studio": Mic,
  "Mixage & mastering": Sliders,
  "Accompagnement artistique": Users,
  "Audiovisuel": Film,
  "Booking": CalendarRange,
  "Projets culturels": Sparkles,
  "Production sonore": Layers,
  "Production phonographique": Radio,
};

const HOME_SERVICES = [
  ["Production musicale", "production-musicale"],
  ["Direction artistique", "direction-artistique"],
  ["Studio", "studio"],
  ["Mixage & mastering", "mixage-mastering"],
  ["Accompagnement artistique", "accompagnement-artistique"],
  ["Audiovisuel", "audiovisuel"],
  ["Booking", "booking"],
  ["Projets culturels", "projets-culturels"],
];

export default function Home() {
  const [artists, setArtists] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data.slice(0, 4))).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={IMG.console} alt="Studio d'enregistrement" className="w-full h-full object-cover opacity-40" />
          <YouTubeBackground videoId={HERO_VIDEO_ID} start={0} end={10} />
          <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/80 to-[#0A0A0C]/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent" />
        </div>

        <Section className="relative z-10 py-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="flex items-center gap-4 mb-6">
              <SoundBars />
              <span className="eyebrow">Label indépendant · Studio · Production</span>
            </div>
            <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-5xl sm:text-7xl lg:text-8xl">
              AL MUSIC<br /><span className="text-gold">RECORDS</span>
            </h1>
            <p className="mt-6 font-display font-semibold text-xl sm:text-2xl tracking-[0.1em] text-slate-200">
              CRÉER. PRODUIRE. DÉVELOPPER.
            </p>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
              AL Music Records accompagne les artistes et les créateurs dans la conception, la production et le développement de leurs projets musicaux et audiovisuels.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/booking-studio" data-testid="hero-reserve-btn" className="btn-gold">🔥 RÉSERVER MA SESSION <ArrowRight size={18} /></Link>
              <Link to="/contact?tab=projet" data-testid="hero-project-btn" className="btn-ghost">SOUMETTRE MON PROJET</Link>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-slate-300"><span className="text-gold font-display font-bold text-lg">À partir de 35 €/h</span> · Studio à Allonnes (72)</div>
          </motion.div>
        </Section>
      </section>

      {/* SERVICES */}
      <Section className="py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow>Notre expertise</Eyebrow>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl">Un savoir-faire complet</h2>
          </div>
          <Link to="/services" className="text-gold text-sm font-semibold flex items-center gap-2 link-underline">
            Tous les services <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOME_SERVICES.map(([name, slug], i) => {
            const Icon = SERVICE_ICONS[name] || Music2;
            return (
              <motion.div
                key={slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
              >
                <Link to={`/services/${slug}`} data-testid={`home-service-${slug}`} className="card-dark p-7 block h-full">
                  <Icon className="text-gold" size={26} />
                  <h3 className="font-display font-bold text-lg mt-5">{name}</h3>
                  <div className="mt-4 text-gold text-xs font-semibold flex items-center gap-1 opacity-0 hover:opacity-100 transition">Découvrir <ArrowRight size={13} /></div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* PACK EN VEDETTE */}
      <Section className="pt-4 pb-8">
        <div className="relative overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/10 via-[#121216] to-red-900/10 p-8 sm:p-12" data-testid="home-pack-banner">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-300 border border-amber-400/30 rounded-full px-3 py-1"><Sparkles size={13} /> Offre en vedette</div>
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center mt-5">
            <div>
              <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl">Pack Prise de voix + Mix + Master</h2>
              <p className="text-slate-300 mt-3 max-w-xl">2 heures de prise de voix + mixage + mastering. Un titre complet, prêt à être diffusé — au meilleur tarif.</p>
              <div className="mt-5 flex items-baseline gap-3">
                <span className="font-display font-black text-4xl text-gold">190 € HT</span>
                <span className="text-slate-400">· 228 € TTC</span>
              </div>
            </div>
            <div>
              <Link to="/booking-studio" data-testid="home-pack-cta" className="btn-gold justify-center w-full lg:w-auto">🔥 RÉSERVER LE PACK <ArrowRight size={18} /></Link>
            </div>
          </div>
        </div>
      </Section>

      {/* STUDIO STRIP */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.micRed} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-[#0A0A0C]/80 to-transparent" />
        </div>
        <Section className="relative z-10">
          <Eyebrow>Le studio</Eyebrow>
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-5xl max-w-2xl">Votre projet. Notre studio.</h2>
          <p className="mt-5 max-w-xl text-slate-300">
            Enregistrement, mixage & mastering, production sonore et audiovisuel. Réservez votre session en ligne.
          </p>
          <Link to="/studio" className="btn-gold mt-8 inline-flex">Découvrir le studio <ArrowRight size={18} /></Link>
        </Section>
      </section>

      {/* ARTISTS */}
      <Section className="py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <Eyebrow>Artistes & Projets</Eyebrow>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl">Le roster</h2>
          </div>
          <Link to="/artistes" className="text-gold text-sm font-semibold flex items-center gap-2 link-underline">Tout voir <ArrowRight size={16} /></Link>
        </div>
        {artists.length === 0 ? (
          <div className="card-dark p-10 text-center text-slate-500 text-sm">
            <span className="font-mono text-xs uppercase tracking-widest text-amber-300">À compléter</span>
            <p className="mt-3">Les artistes et projets seront présentés ici depuis l'administration.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {artists.map((a) => (
              <Link key={a.id} to="/artistes" className="card-dark overflow-hidden group">
                <div className="aspect-[3/4] overflow-hidden bg-[#1A1A20]">
                  {a.photo ? <img src={a.photo} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" /> : <div className="w-full h-full flex items-center justify-center text-slate-700"><Music2 size={40} /></div>}
                </div>
                <div className="p-4">
                  <h3 className="font-display font-bold">{a.name}</h3>
                  {a.genre && <p className="text-xs text-slate-500 mt-1">{a.genre}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* NEWS */}
      {news.length > 0 && (
        <Section className="pb-24">
          <Eyebrow>Actualités</Eyebrow>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl mb-12">Dernières nouvelles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {news.map((n) => (
              <Link key={n.id} to={`/actualites/${n.slug}`} className="card-dark overflow-hidden group">
                <div className="aspect-video overflow-hidden bg-[#1A1A20]">
                  {n.image && <img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
                </div>
                <div className="p-6">
                  {n.category && <span className="eyebrow">{n.category}</span>}
                  <h3 className="font-display font-bold text-lg mt-2">{n.title}</h3>
                  <p className="text-sm text-slate-500 mt-2 line-clamp-2">{n.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* CTA */}
      <section className="relative py-28 border-y border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-red-900/10" />
        <Section className="relative z-10 text-center">
          <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl">Vous avez un projet ?<br /><span className="text-gold">Parlons-en.</span></h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact?tab=projet" className="btn-gold">SOUMETTRE MON PROJET</Link>
            <Link to="/booking-studio" className="btn-ghost">RÉSERVER LE STUDIO</Link>
          </div>
        </Section>
      </section>
    </div>
  );
}
